/** biome-ignore-all lint/suspicious/noExplicitAny: <Fix Type Later> */
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CuriosityDisclaimer } from "@/components/curiosity-disclaimer/CuriosityDisclaimer";
import { useCuriosityDisclaimer } from "@/components/curiosity-disclaimer/useCuriosityDisclaimer";
import { Avatar, DropdownMenu } from "@/components/UI";
import { useSearch } from "@/features/search/hooks/useSearch";
import { RootState } from "@/lib/store/dynamicStore";
import { useFeatureFlagStore } from "@/lib/store/featureFlagStore";
import { logoutUser } from "@/lib/store/features/auth/authSlice";
import { canAccessAdmin, canAccessSearch } from "@/utils/roles";
import { HeaderNav } from "./header.model";

export interface IHeaderNav {
	title: string;
	path: string;
}

export function Header() {
	const navigate = useNavigate();
	const searchInputRef = useRef<HTMLInputElement>(null);
	const user = useSelector((state: RootState) => state.auth.user);
	const [toggleBurger, setToggleBurger] = useState(false);
	const [toggleSearchBar, setToggleSearchBar] = useState(false);
	const { searchQuery, setSearchQuery } = useSearch();
	const {
		isOpen: isCuriosityDisclaimerOpen,
		setIsOpen: setIsCuriosityDisclaimerOpen,
	} = useCuriosityDisclaimer(["/curiosity", "/curiosity/*"]);
	const dispatch = useDispatch();
	const { pathname } = useLocation();
	const flags = useFeatureFlagStore((state) => state.flags);

	const flaggedPaths: Record<string, boolean> = {
		"/curiosity": flags.curiosity,
		"/abacus": flags.abacus,
		"/how-to": flags.how_to,
	};

	const filteredNav = HeaderNav.filter(
		(item) => !(item.path in flaggedPaths) || flaggedPaths[item.path],
	);

	// console.log(user);

	const handleClick = (route: string) => {
		navigate(route);
	};

	const handleSearchSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (searchQuery.trim()) {
			navigate(`/search/${encodeURIComponent(searchQuery)}`);
			setToggleSearchBar(false);
			setSearchQuery("");
		}
	};

	const handleLogout = () => {
		dispatch(logoutUser() as any);
		// Navigation will be handled by MSAL redirect
	};

	useEffect(() => {
		if (toggleSearchBar && searchInputRef.current) {
			searchInputRef.current.focus();
		}
	}, [toggleSearchBar]);

	return (
		<>
			<CuriosityDisclaimer
				isOpen={isCuriosityDisclaimerOpen}
				onClose={() => setIsCuriosityDisclaimerOpen(false)}
			/>
			<header
				className="flex items-center h-header w-full fixed top-0 left-0 z-[10] bg-grayscale-white mx-auto animate-headerSlide [animation-delay:300ms] [animation-duration:400ms]"
				style={{
					marginTop: isCuriosityDisclaimerOpen ? "32px" : "0px",
					opacity: 0,
				}}
			>
				<div className="flex justify-between items-center max-w-[90%] px-40 w-full mx-auto">
					<div className="flex items-center gap-24">
						<a
							href="/"
							onClick={(e) => {
								e.preventDefault();
								handleClick("/");
							}}
							className="flex items-center gap-8 p-8 -m-8 text-[18px] font-semibold cursor-pointer transition-all duration-200 hover:translate-x-[6px] no-underline text-grayscale-950 hover:text-grayscale-950 rounded-24 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none"
						>
							<img
								className="w-[20px] h-[20px] bg-primary-600 rounded-full p-6"
								src="/logo.svg"
								alt=""
							/>
							Einstein
						</a>
						<div className="h-16 w-[1px] bg-grayscale-200"></div>
						<nav className="hidden md:flex items-center gap-8 -ml-[8px] font-rubik text-[14px] font-medium">
							{filteredNav.map((navItem) => {
								// Debugging logs for active state
								const isRoot = navItem.path === "/";
								const isCurrentPathRoot = pathname === "/";
								const isRootMatch = isRoot && isCurrentPathRoot;
								const isSubPathMatch =
									!isRoot && pathname.startsWith(navItem.path);
								const isActive = isRootMatch || isSubPathMatch;

								// // console.log(
								//   `NavLink Check: Item Path="${navItem.path}", Current Path="${pathname}", isRootMatch=${isRootMatch}, isSubPathMatch=${isSubPathMatch}, isActive=${isActive}`
								// );

								return (
									<a
										key={navItem.path}
										href={navItem.path}
										onClick={(e) => {
											e.preventDefault();
											handleClick(navItem.path);
										}}
										className={clsx(
											"flex justify-center items-center gap-8 cursor-pointer text-center rounded-32 outline-none px-8 py-8 min-w-[100px] font-rubik font-medium text-[14px] transition-all duration-200 hover:-translate-y-[2px] select-none whitespace-nowrap no-underline focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none",
											isActive
												? "bg-primary-100 text-grayscale-900"
												: "bg-transparent text-grayscale-700 hover:bg-grayscale-50 hover:text-grayscale-700",
										)}
									>
										{navItem.title}
									</a>
								);
							})}
						</nav>
					</div>

					<div className="flex items-center gap-8">
						{flags.elastic_search && user && canAccessSearch(user) && (
							<form
								onSubmit={handleSearchSubmit}
								className="relative flex items-center"
							>
								<img
									className="absolute left-12 top-1/2 -translate-y-1/2 w-[20px] h-[20px] z-[1] pointer-events-none"
									src="/icons/search.svg"
									alt=""
								/>
								<input
									className="pl-40 pr-12 py-6 w-0 text-[14px] font-rubik bg-grayscale-50 border border-grayscale-950 rounded-24 outline-none transition-all duration-100 focus:outline-none focus:ring-1 focus:ring-grayscale-950 focus:border-transparent animate-headerSearch"
									ref={searchInputRef}
									type="text"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder="Search network drives and Sharepoint"
								/>
								<button type="submit" hidden />
							</form>
						)}
						<DropdownMenu placement="bottom-end">
							<DropdownMenu.Trigger
								aria-label="User menu"
								className="rounded-full border-2 border-primary-500 transition-all duration-200 hover:bg-primary-500 cursor-pointer focus-visible:outline-2 focus-visible:outline-primary-300 focus-visible:outline-offset-2"
							>
								<Avatar
									name={user?.name || ""}
									size="sm"
									bgColor="bg-grayscale-white"
									textColor="text-primary-700"
									className="hover:scale-100"
								/>
							</DropdownMenu.Trigger>
							<DropdownMenu.Content>
								{canAccessAdmin(user) && (
									<DropdownMenu.Item onSelect={() => handleClick("/admin")}>
										Admin
									</DropdownMenu.Item>
								)}
								<DropdownMenu.Item variant="danger" onSelect={handleLogout}>
									Logout
								</DropdownMenu.Item>
							</DropdownMenu.Content>
						</DropdownMenu>

						<div
							className="flex flex-col justify-between h-16 rounded-4 p-4 ml-12 bg-transparent border-none cursor-pointer focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none md:hidden"
							onClick={() => setToggleBurger(!toggleBurger)}
						>
							{[0, 1, 2].map((item) => (
								<div
									key={item}
									className={clsx(
										"w-[20px] h-[2px] bg-grayscale-950 transition-all duration-200",
										toggleBurger && {
											"first:rotate-[-45deg] first:translate-x-[-7px] first:translate-y-[3px]":
												item === 0,
											"opacity-0": item === 1,
											"last:rotate-[45deg] last:translate-x-[-7px] last:translate-y-[-3px]":
												item === 2,
										},
									)}
								></div>
							))}
						</div>
					</div>
				</div>

				{toggleBurger && (
					<div className="fixed z-[30] top-header h-[calc(100dvh-64px)] w-full bg-grayscale-white">
						<nav className="flex flex-col gap-16 p-24 px-40 pt-24">
							{filteredNav.map((navItem, index) => (
								<a
									key={navItem.path}
									href={navItem.path}
									className="min-w-0 w-max p-8 px-0 opacity-0 origin-top animate-rotateX text-left font-rubik text-[16px] font-normal text-grayscale-700 hover:text-primary-500 transition-colors duration-200 no-underline rounded-4 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none focus-visible:bg-grayscale-50"
									style={{
										animationDelay: `${200 + index * 90}ms`,
										animationFillMode: "forwards",
									}}
									onClick={(e) => {
										e.preventDefault();
										handleClick(navItem.path);
										setToggleBurger(false);
									}}
								>
									{navItem.title}
								</a>
							))}
						</nav>
					</div>
				)}
			</header>
		</>
	);
}
